import axios from 'axios';
import { saveAs } from 'file-saver';

import { API_URL } from 'utils/http';
import { security } from 'utils/security';
import {
  CliEditRequest,
  CliTarget,
  CliTerminalCommands,
  CliTerminalError,
  CliTerminalSession,
  CliTerminalSessions,
  CliTerminalTargets,
  CliUploadProgress,
  CliUploadRequest,
} from 'types/cli';

// `exec` and `upload` use fetch, not axios: axios buffers a whole response, and `tail -f` never ends.

const terminalUrl = ({
  userId,
  instanceName,
}: {
  userId: string;
  instanceName: string;
}) => `${API_URL}/cli/${userId}/instances/${instanceName}/terminal`;

const authHeader = async () => {
  const token = await security.getAccessTokenSilently()({
    audience: process.env.REACT_APP_AUTH_AUDIENCE,
  });
  return `Bearer ${token}`;
};

// Without a token it resumes a session whose key is still valid.
export function startCliTerminalSession({
  userId,
  instanceName,
  clusterAccessToken,
}: {
  userId: string;
  instanceName: string;
  clusterAccessToken?: string;
}) {
  return axios.post<CliTerminalSession>(
    `${terminalUrl({ userId, instanceName })}/session`,
    { clusterAccessToken },
  );
}

export function startCliTerminalSessionByToken({
  userId,
  clusterAccessToken,
}: {
  userId: string;
  clusterAccessToken: string;
}) {
  return axios.post<CliTerminalSession>(
    `${API_URL}/cli/${userId}/terminal/session`,
    { clusterAccessToken },
  );
}

export function endCliTerminalSession({
  userId,
  instanceName,
}: {
  userId: string;
  instanceName: string;
}) {
  return axios.delete(`${terminalUrl({ userId, instanceName })}/session`);
}

export function getCliTerminalSessions({ userId }: { userId: string }) {
  return axios.get<CliTerminalSessions>(
    `${API_URL}/cli/${userId}/terminal/sessions`,
  );
}

export function getCliTerminalCatalog({ userId }: { userId: string }) {
  return axios.get<CliTerminalCommands>(
    `${API_URL}/cli/${userId}/terminal/catalog`,
  );
}

export function getCliTerminalCommands({
  userId,
  instanceName,
}: {
  userId: string;
  instanceName: string;
}) {
  return axios.get<CliTerminalCommands>(
    `${terminalUrl({ userId, instanceName })}/commands`,
  );
}

export function getCliTerminalTargets({
  userId,
  instanceName,
}: {
  userId: string;
  instanceName: string;
}) {
  return axios.get<CliTerminalTargets>(
    `${terminalUrl({ userId, instanceName })}/targets`,
  );
}

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const terminalFailure = async (response: Response, fallback: string) => {
  const failure = await response.json().catch(() => ({}));
  const error: CliTerminalError = new Error(
    failure.error ?? failure.message ?? `${fallback} (${response.status}).`,
  );
  error.code = failure.code;
  error.status = response.status;
  return error;
};

const filenameFromDisposition = (disposition: string) => {
  const quoted = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return quoted ? decodeURIComponent(quoted[1]) : '';
};

// Besides output, a line can answer with a .tgz to save, or an upload or edit for the browser to finish.
export async function runCliTerminalCommand({
  userId,
  instanceName,
  line,
  target,
  cwd,
  columns,
  onChunk,
  signal,
}: {
  userId: string;
  instanceName: string;
  line: string;
  target?: CliTarget;
  cwd?: string;
  columns?: number;
  onChunk: (text: string) => void;
  signal: AbortSignal;
}): Promise<CliUploadRequest | CliEditRequest | void> {
  const response = await fetch(
    `${terminalUrl({ userId, instanceName })}/exec`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: await authHeader(),
      },
      body: JSON.stringify({ line, target, cwd, columns }),
      signal,
    },
  );

  if (!response.ok) throw await terminalFailure(response, 'Command failed');

  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.startsWith('application/json')) {
    const answer = await response.json();
    if (answer?.code === 'CLI_UPLOAD_REQUIRED') {
      return {
        kind: 'upload',
        dest: answer.dest,
        target: answer.target ?? null,
        localPath: answer.localPath ?? null,
      };
    }
    if (answer?.code === 'CLI_EDIT_REQUIRED') {
      const file = answer.file ?? {};
      return {
        kind: 'edit',
        path: file.path,
        size: file.size ?? 0,
        mode: file.mode ?? null,
        content: file.content ?? '',
        target: answer.target ?? null,
      };
    }
    onChunk(
      typeof answer?.body === 'string'
        ? answer.body
        : JSON.stringify(answer, null, 2),
    );
    return;
  }

  // A cluster-level command runs wherever it belongs, not on the tab it was typed in. Saying so
  // keeps the transcript honest about which pod answered.
  const ranOn = response.headers.get('x-cli-target');
  const onThisTab =
    target &&
    (ranOn === target.label || ranOn === `self-managed ${target.label}`);
  if (ranOn && !onThisTab) onChunk(`→ ${ranOn}\n`);

  if (contentType.startsWith('application/gzip')) {
    const blob = await response.blob();
    const filename =
      filenameFromDisposition(
        response.headers.get('content-disposition') ?? '',
      ) || 'download.tgz';
    saveAs(blob, filename);
    onChunk(
      `Downloaded ${filename} (${formatBytes(blob.size)}). It is a .tgz -- extract it to get the original files.\n`,
    );
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) return;

  const decoder = new TextDecoder();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    onChunk(decoder.decode(value, { stream: true }));
  }
  const tail = decoder.decode();
  if (tail) onChunk(tail);
}

// Matches the terminal's own quoting: cloud-server's tokenizer reads '...' as one word.
const quoteCliArgument = (value: string) =>
  /[\s"']/.test(value) ? `'${value.replace(/'/g, `'"'"'`)}'` : value;

// Under the Portal's 30MB: Cloud Run refuses bodies over 32MB, and this backend holds each part twice.
const UPLOAD_PART_BYTES = 8_000_000;
const UPLOAD_ID_ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789';

// cli-server's own client builds the id this way: a unix timestamp plus ten lowercase alphanumerics.
const uploadId = () =>
  `${Math.floor(Date.now() / 1000)}${Array.from(
    crypto.getRandomValues(new Uint8Array(10)),
    byte => UPLOAD_ID_ALPHABET[byte % 36],
  ).join('')}`;

export async function uploadCliTerminalFile({
  userId,
  instanceName,
  line,
  target,
  cwd,
  file,
  onProgress,
  signal,
}: {
  userId: string;
  instanceName: string;
  line: string;
  target?: CliTarget;
  cwd?: string;
  file: File;
  onProgress?: (progress: CliUploadProgress) => void;
  signal: AbortSignal;
}): Promise<{ body: string; dest: string; target: string | null }> {
  const base = terminalUrl({ userId, instanceName });
  const hash = uploadId();
  const parts = Math.max(1, Math.ceil(file.size / UPLOAD_PART_BYTES));
  const common: Record<string, string> = {
    line,
    hash,
    original_file_name: file.name,
  };
  if (target) common.target = JSON.stringify(target);
  if (cwd) common.cwd = cwd;

  for (let index = 0; index < parts; index += 1) {
    const form = new FormData();
    form.append(
      'file',
      file.slice(index * UPLOAD_PART_BYTES, (index + 1) * UPLOAD_PART_BYTES),
      file.name,
    );
    const response = await fetch(
      `${base}/upload?${new URLSearchParams({ ...common, index: String(index) })}`,
      {
        method: 'POST',
        headers: { Authorization: await authHeader() },
        body: form,
        signal,
      },
    );
    if (!response.ok) {
      throw await terminalFailure(
        response,
        `Upload of ${file.name} failed at part ${index + 1} of ${parts}`,
      );
    }
    onProgress?.({
      sent: Math.min(file.size, (index + 1) * UPLOAD_PART_BYTES),
      total: file.size,
      part: index + 1,
      parts,
    });
  }

  const merged = await fetch(`${base}/upload/merge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: await authHeader(),
    },
    body: JSON.stringify({
      line,
      target,
      cwd,
      hash,
      total_chunks: parts,
      original_file_name: file.name,
    }),
    signal,
  });
  if (!merged.ok)
    throw await terminalFailure(merged, `Upload of ${file.name} failed`);
  return merged.json();
}

export async function saveCliTerminalFile({
  userId,
  instanceName,
  path,
  target,
  content,
  signal,
}: {
  userId: string;
  instanceName: string;
  path: string;
  target?: CliTarget;
  content: string;
  signal: AbortSignal;
}): Promise<{ body: string; path: string; target: string | null }> {
  // The line is sent, not just the path: the server re-parses it, so the save is authorized and
  // routed to exactly the pod the open was.
  const response = await fetch(
    `${terminalUrl({ userId, instanceName })}/edit`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: await authHeader(),
      },
      body: JSON.stringify({
        line: `edit ${quoteCliArgument(path)}`,
        target,
        content,
      }),
      signal,
    },
  );

  if (!response.ok)
    throw await terminalFailure(response, `Saving ${path} failed`);
  return response.json();
}
